import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  User,
  Bell,
  Shield,
  Palette,
  Globe,
  Building2,
  Users,
  UserRound,
  Briefcase,
  RefreshCw,
  Sparkles,
  AlertTriangle,
  Check,
  ExternalLink,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useAuth } from '@/lib/contexts/auth-context'
import { useTheme } from '@/lib/contexts/theme-context'
import { useToast } from '@/components/ui/use-toast'
import { PageHeader, FluentCard } from '@/components/layout/page-header'
import { getAvatarProps } from '@/lib/avatar-colors'
import {
  settingsService,
  type UserProfile,
  type NotificationSettings,
  type AppearanceSettings,
  type WorkspaceSettings,
} from '@/lib/api/services/settings'
import { industryPresetsService, type IndustryPresetSummary } from '@/lib/api/services/industryPresets'

// Constants for workspace settings
const ACCOUNT_TYPES = [
  { value: 'Personal', label: 'Personal', icon: UserRound, description: 'For individual use' },
  { value: 'Team', label: 'Team', icon: Users, description: 'For small to medium teams' },
  { value: 'Enterprise', label: 'Enterprise', icon: Building2, description: 'For large organizations' },
]

const COMPANY_SIZES = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '501-1000', label: '501-1000 employees' },
  { value: '1000+', label: '1000+ employees' },
]

const INDUSTRIES = [
  'General',
  'Legal',
  'Finance',
  'Healthcare',
  'Technology',
  'Marketing',
  'Real Estate',
  'Education',
  'Consulting',
  'Manufacturing',
  'Retail',
  'Insurance',
  'Non-Profit',
  'Government',
]

export default function Settings() {
  const { user, refreshUser } = useAuth()
  const { theme, setTheme } = useTheme()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [notifications, setNotifications] = useState<NotificationSettings | null>(null)
  const [appearance, setAppearance] = useState<AppearanceSettings | null>(null)
  const [workspace, setWorkspace] = useState<WorkspaceSettings | null>(null)
  const [presets, setPresets] = useState<IndustryPresetSummary[]>([])
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
  })
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [workspaceFormData, setWorkspaceFormData] = useState({
    accountType: '',
    companyName: '',
    companyIndustry: '',
    companySize: '',
    companyWebsite: '',
  })

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true)
      const [profileData, notificationData, appearanceData, workspaceData, presetsData] = await Promise.all([
        settingsService.getProfile(),
        settingsService.getNotificationSettings(),
        settingsService.getAppearanceSettings(),
        settingsService.getWorkspaceSettings().catch(() => null),
        industryPresetsService.getAll().catch(() => []),
      ])

      setProfile(profileData)
      setNotifications(notificationData)
      setAppearance(appearanceData)
      setWorkspace(workspaceData)
      setPresets(presetsData)
      setFormData({
        firstName: profileData.firstName || '',
        lastName: profileData.lastName || '',
      })
      if (workspaceData) {
        setWorkspaceFormData({
          accountType: workspaceData.accountType || '',
          companyName: workspaceData.companyName || '',
          companyIndustry: workspaceData.companyIndustry || '',
          companySize: workspaceData.companySize || '',
          companyWebsite: workspaceData.companyWebsite || '',
        })
      }
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to load settings',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)

    try {
      await settingsService.updateProfile(formData)
      await refreshUser()
      toast({
        title: 'Success',
        description: 'Profile updated successfully',
      })
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to update profile',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive',
      })
      return
    }

    setSaving(true)
    try {
      await settingsService.changePassword(passwordData)
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      toast({
        title: 'Success',
        description: 'Password changed successfully',
      })
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to change password',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleNotificationChange = async (key: keyof NotificationSettings, value: boolean) => {
    try {
      const updated = await settingsService.updateNotificationSettings({ [key]: value })
      setNotifications(updated)
      toast({
        title: 'Success',
        description: 'Notification settings updated',
      })
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to update notification settings',
        variant: 'destructive',
      })
    }
  }

  const handleAppearanceChange = async (key: keyof AppearanceSettings, value: string) => {
    try {
      // If changing theme, use the theme context for immediate effect
      if (key === 'theme') {
        await setTheme(value as 'light' | 'dark' | 'system')
        setAppearance(prev => prev ? { ...prev, theme: value as 'light' | 'dark' | 'system' } : null)
      } else {
        const updated = await settingsService.updateAppearanceSettings({ [key]: value })
        setAppearance(updated)
      }
      toast({
        title: 'Success',
        description: 'Appearance settings updated',
      })
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to update appearance settings',
        variant: 'destructive',
      })
    }
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      await settingsService.uploadAvatar(file)
      await refreshUser()
      loadSettings()
      toast({
        title: 'Success',
        description: 'Avatar updated successfully',
      })
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to upload avatar',
        variant: 'destructive',
      })
    }
  }

  // Workspace handlers
  const handleWorkspaceFieldChange = async (field: keyof typeof workspaceFormData, value: string) => {
    const updatedForm = { ...workspaceFormData, [field]: value }
    setWorkspaceFormData(updatedForm)

    try {
      const updated = await settingsService.updateWorkspaceSettings({ [field]: value })
      setWorkspace(updated)
      toast({
        title: 'Settings updated',
        description: `${field.replace(/([A-Z])/g, ' $1').toLowerCase()} has been updated.`,
      })
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to update workspace settings',
        variant: 'destructive',
      })
    }
  }

  const handleApplyPreset = async (presetId: string) => {
    try {
      setSaving(true)
      const result = await settingsService.applyPreset({
        presetId,
        includeDefaultAgents: workspace?.includesDefaultAgents ?? true,
      })
      if (result.success) {
        toast({
          title: 'Preset applied',
          description: result.message,
        })
        loadSettings()
      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        })
      }
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to apply preset',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleResetToDefaults = async () => {
    try {
      setSaving(true)
      const result = await settingsService.resetToDefaults()
      if (result.success) {
        toast({
          title: 'Reset complete',
          description: result.message,
        })
        loadSettings()
      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        })
      }
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to reset workspace',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleRerunWizard = async () => {
    try {
      setSaving(true)
      const result = await settingsService.rerunSetupWizard()
      if (result.success) {
        toast({
          title: 'Wizard reset',
          description: result.message,
        })
        navigate('/onboarding')
      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        })
      }
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to reset setup wizard',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full rounded-none" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        description="Manage your account settings and preferences"
      />

      <Tabs defaultValue="profile" className="space-y-6">
        <div className="bg-white dark:bg-gray-900/50 rounded-2xl p-1.5 inline-flex border border-gray-100 dark:border-gray-800" style={{ boxShadow: '0 0 2px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)' }}>
          <TabsList className="bg-transparent border-0 gap-1">
            <TabsTrigger value="profile" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-gray-900 rounded-xl px-4 py-2">
              <User className="mr-2 h-4 w-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-gray-900 rounded-xl px-4 py-2">
              <Bell className="mr-2 h-4 w-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-gray-900 rounded-xl px-4 py-2">
              <Shield className="mr-2 h-4 w-4" />
              Security
            </TabsTrigger>
            <TabsTrigger value="appearance" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-gray-900 rounded-xl px-4 py-2">
              <Palette className="mr-2 h-4 w-4" />
              Appearance
            </TabsTrigger>
            <TabsTrigger value="workspace" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white dark:data-[state=active]:bg-white dark:data-[state=active]:text-gray-900 rounded-xl px-4 py-2">
              <Briefcase className="mr-2 h-4 w-4" />
              Workspace
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="profile" className="space-y-6">
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
                <User className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Profile Information</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Update your personal information and profile picture</p>
              </div>
            </div>
            <form onSubmit={handleSaveProfile} className="space-y-6">
              <div className="flex items-center gap-6 p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/50">
                {(() => {
                  const { initials, colorClasses } = getAvatarProps({
                    id: user?.id,
                    firstName: user?.firstName,
                    lastName: user?.lastName,
                    email: user?.email || profile?.email,
                  })
                  return (
                    <Avatar className="h-20 w-20 rounded-full">
                      {profile?.avatarUrl && <AvatarImage src={profile.avatarUrl} alt={profile.email} className="rounded-full" />}
                      <AvatarFallback className={`rounded-full text-xl font-semibold ${colorClasses}`}>{initials}</AvatarFallback>
                    </Avatar>
                  )
                })()}
                <div>
                  <input
                    type="file"
                    id="avatar-upload"
                    className="hidden"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                  />
                  <Button variant="outline" size="sm" className="rounded-xl" asChild>
                    <label htmlFor="avatar-upload" className="cursor-pointer">
                      Change Avatar
                    </label>
                  </Button>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    JPG, PNG or GIF. Max size 2MB
                  </p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="John"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Doe"
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile?.email || user?.email || ''}
                  disabled
                  className="rounded-xl"
                />
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={saving} className="rounded-full px-6">
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </FluentCard>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600">
                <Bell className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Email Notifications</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Choose what emails you want to receive</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="space-y-0.5">
                  <Label className="text-gray-900 dark:text-white">Marketing Emails</Label>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Receive emails about new features and updates
                  </p>
                </div>
                <Switch
                  checked={notifications?.marketingEmails || false}
                  onCheckedChange={(checked) =>
                    handleNotificationChange('marketingEmails', checked)
                  }
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="space-y-0.5">
                  <Label className="text-gray-900 dark:text-white">Security Alerts</Label>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Receive alerts about your account security
                  </p>
                </div>
                <Switch
                  checked={notifications?.securityAlerts || false}
                  onCheckedChange={(checked) =>
                    handleNotificationChange('securityAlerts', checked)
                  }
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="space-y-0.5">
                  <Label className="text-gray-900 dark:text-white">Template Updates</Label>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Get notified when templates are updated
                  </p>
                </div>
                <Switch
                  checked={notifications?.templateUpdates || false}
                  onCheckedChange={(checked) =>
                    handleNotificationChange('templateUpdates', checked)
                  }
                />
              </div>
            </div>
          </FluentCard>

          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-400 to-purple-600">
                <Bell className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Push Notifications</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Manage your in-app notifications</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="space-y-0.5">
                  <Label className="text-gray-900 dark:text-white">Desktop Notifications</Label>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Show desktop notifications for important events
                  </p>
                </div>
                <Switch
                  checked={notifications?.desktopNotifications || false}
                  onCheckedChange={(checked) =>
                    handleNotificationChange('desktopNotifications', checked)
                  }
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="space-y-0.5">
                  <Label className="text-gray-900 dark:text-white">Sound Effects</Label>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Play sounds for notifications
                  </p>
                </div>
                <Switch
                  checked={notifications?.soundEffects || false}
                  onCheckedChange={(checked) =>
                    handleNotificationChange('soundEffects', checked)
                  }
                />
              </div>
            </div>
          </FluentCard>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Password</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Update your password to keep your account secure</p>
              </div>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  placeholder="Enter current password"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, currentPassword: e.target.value })
                  }
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="Enter new password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, newPassword: e.target.value })
                  }
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm new password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                  }
                  className="rounded-xl"
                />
              </div>
              <Button type="submit" disabled={saving} className="rounded-full px-6">
                {saving ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          </FluentCard>

          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-rose-400 to-pink-600">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Two-Factor Authentication</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Add an extra layer of security to your account</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
              <div className="space-y-0.5">
                <Label className="text-gray-900 dark:text-white">Enable 2FA</Label>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Require a verification code in addition to your password
                </p>
              </div>
              <Switch />
            </div>
          </FluentCard>
        </TabsContent>

        <TabsContent value="appearance" className="space-y-6">
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600">
                <Palette className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Theme</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Customize the appearance of the application</p>
              </div>
            </div>
            <div className="space-y-4">
              <Label>Color Theme</Label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  className={`flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    theme === 'light'
                      ? 'border-gray-900 dark:border-white bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  onClick={() => handleAppearanceChange('theme', 'light')}
                >
                  <div className="h-5 w-5 rounded-full bg-gradient-to-br from-amber-200 to-yellow-400" />
                  <span className="font-medium">Light</span>
                </button>
                <button
                  className={`flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    theme === 'dark'
                      ? 'border-gray-900 dark:border-white bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  onClick={() => handleAppearanceChange('theme', 'dark')}
                >
                  <div className="h-5 w-5 rounded-full bg-gradient-to-br from-gray-700 to-gray-900" />
                  <span className="font-medium">Dark</span>
                </button>
                <button
                  className={`flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    theme === 'system'
                      ? 'border-gray-900 dark:border-white bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  onClick={() => handleAppearanceChange('theme', 'system')}
                >
                  <Globe className="h-5 w-5" />
                  <span className="font-medium">System</span>
                </button>
              </div>
            </div>
          </FluentCard>

          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-400 to-violet-600">
                <Globe className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Language</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Choose your preferred language</p>
              </div>
            </div>
            <select
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-sm focus:border-gray-900 dark:focus:border-white focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/10 text-gray-900 dark:text-white"
              value={appearance?.language || 'en'}
              onChange={(e) => handleAppearanceChange('language', e.target.value)}
            >
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
              <option value="ja">日本語</option>
            </select>
          </FluentCard>
        </TabsContent>

        {/* Workspace Tab */}
        <TabsContent value="workspace" className="space-y-6">
          {/* Account Type Section */}
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-600">
                <UserRound className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Account Type</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Your current account type determines available features</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {ACCOUNT_TYPES.map((type) => {
                const Icon = type.icon
                const isSelected = workspaceFormData.accountType === type.value
                return (
                  <button
                    key={type.value}
                    onClick={() => handleWorkspaceFieldChange('accountType', type.value)}
                    className={`relative flex flex-col items-center p-6 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute top-3 right-3">
                        <Check className="h-5 w-5 text-primary" />
                      </div>
                    )}
                    <div className={`p-3 rounded-xl ${isSelected ? 'bg-primary/10' : 'bg-gray-100 dark:bg-gray-800'}`}>
                      <Icon className={`h-6 w-6 ${isSelected ? 'text-primary' : 'text-gray-600 dark:text-gray-400'}`} />
                    </div>
                    <span className={`mt-3 font-medium ${isSelected ? 'text-primary' : 'text-gray-900 dark:text-white'}`}>
                      {type.label}
                    </span>
                    <span className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {type.description}
                    </span>
                  </button>
                )
              })}
            </div>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              You can change your account type at any time. Upgrading to Team or Enterprise unlocks additional features.
            </p>
          </FluentCard>

          {/* Company Information Section */}
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-600">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Company Information</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Details about your organization</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name</Label>
                  <Input
                    id="companyName"
                    value={workspaceFormData.companyName}
                    onChange={(e) => setWorkspaceFormData({ ...workspaceFormData, companyName: e.target.value })}
                    onBlur={() => handleWorkspaceFieldChange('companyName', workspaceFormData.companyName)}
                    placeholder="Acme Corp"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyWebsite">Website</Label>
                  <Input
                    id="companyWebsite"
                    value={workspaceFormData.companyWebsite}
                    onChange={(e) => setWorkspaceFormData({ ...workspaceFormData, companyWebsite: e.target.value })}
                    onBlur={() => handleWorkspaceFieldChange('companyWebsite', workspaceFormData.companyWebsite)}
                    placeholder="https://example.com"
                    className="rounded-xl"
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="companyIndustry">Industry</Label>
                  <Select
                    value={workspaceFormData.companyIndustry}
                    onValueChange={(value) => handleWorkspaceFieldChange('companyIndustry', value)}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((industry) => (
                        <SelectItem key={industry} value={industry}>
                          {industry}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companySize">Company Size</Label>
                  <Select
                    value={workspaceFormData.companySize}
                    onValueChange={(value) => handleWorkspaceFieldChange('companySize', value)}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPANY_SIZES.map((size) => (
                        <SelectItem key={size.value} value={size.value}>
                          {size.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </FluentCard>

          {/* Industry Preset Section */}
          <FluentCard>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-400 to-purple-600">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Industry Preset</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Pre-configured agents, workflows, and templates for your industry</p>
                </div>
              </div>
              {workspace?.appliedPresetName && (
                <Badge variant="secondary" className="text-sm">
                  Currently: {workspace.appliedPresetName}
                </Badge>
              )}
            </div>

            {workspace?.appliedPresetId ? (
              <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{workspace.appliedPresetName}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {workspace.includesDefaultAgents ? 'Includes default Templatean agents' : 'Custom agents only'}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl"
                    onClick={() => navigate('/presets')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Preset
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 mb-4">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  No industry preset is currently applied. Select a preset below to get started with industry-specific agents and workflows.
                </p>
              </div>
            )}

            <div className="space-y-3">
              <Label>Available Presets</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-80 overflow-y-auto">
                {presets.length > 0 ? (
                  presets.map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => handleApplyPreset(preset.id)}
                      disabled={saving || workspace?.appliedPresetId === preset.id}
                      className={`flex flex-col items-start p-4 rounded-xl border transition-all text-left ${
                        workspace?.appliedPresetId === preset.id
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">{preset.icon}</span>
                        <span className="font-medium text-gray-900 dark:text-white">{preset.name}</span>
                        {workspace?.appliedPresetId === preset.id && (
                          <Check className="h-4 w-4 text-primary ml-auto" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                        {preset.description}
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          {preset.agentCount} agents
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {preset.workflowCount} workflows
                        </Badge>
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 col-span-full text-center py-8">
                    No presets available. Contact support for custom presets.
                  </p>
                )}
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Changing your preset will add new agents and workflows but won&apos;t delete your existing custom configurations.
            </p>
          </FluentCard>

          {/* Reset Options Section */}
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-orange-400 to-red-600">
                <RefreshCw className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Reset Options</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Reset your workspace to start fresh</p>
              </div>
            </div>
            <div className="space-y-4">
              {/* Reset to Defaults */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="space-y-0.5">
                  <Label className="text-gray-900 dark:text-white">Reset to Default Agents</Label>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Remove custom agents and restore the 6 default Templatean agents
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-xl">
                      Reset Agents
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Reset to Default Agents?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will remove all custom agents and restore the 6 default Templatean agents (Alex, Scarlet, Sia, Olga, Dimi, Andrew). Your templates and documents will remain unchanged.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleResetToDefaults}>
                        Reset Agents
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {/* Re-run Setup Wizard */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                <div className="space-y-0.5">
                  <Label className="text-gray-900 dark:text-white">Re-run Setup Wizard</Label>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Go through the onboarding wizard again to reconfigure your workspace
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-xl">
                      Re-run Wizard
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Re-run Setup Wizard?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will reset your onboarding status and redirect you to the setup wizard. You&apos;ll be able to reconfigure your account type, company info, and industry preset. Existing data will not be deleted.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleRerunWizard}>
                        Start Wizard
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {/* Factory Reset Warning */}
              <div className="p-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">Need a complete reset?</p>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      For a full factory reset that removes all data, please contact support. This action is irreversible.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </FluentCard>

          {/* Onboarding Status */}
          {workspace?.onboardingCompletedAt && (
            <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
              Onboarding completed on {new Date(workspace.onboardingCompletedAt).toLocaleDateString()}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
